import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import { PATHS } from "../../config/permissionsPaths";
import { AuthContext } from "../../context/AuthContext";
import useAuth from "../../hooks/useAuth";
import { FaPowerOff } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import routes from "../../routes/routes";
import { ROUTE_PERMISSIONS } from "../../routes/routePermissions";
import icons from "../../assets/icons/iconMap";
import { menuConfig } from "../../config/menuConfig";
import { canAccessByRule, resolveMenuItemRule, validateMenuAgainstRoutes } from "../../utils/menuPermissions";
import CommandPalette from "./CommandPalette";

const NavbarContainer = styled.nav`
  width: 100%;
  height: var(--navbar-height);
  background-color: ${(props) => props.theme.navbarBackground};
  color: ${(props) => props.theme.navbarText};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  font-size: var(--app-font-size);
  border-bottom: 1px solid #e2e8f0;
  box-sizing: border-box;
`;

const Menu = styled.ul`
  list-style: none;
  display: flex;
  gap: 20px;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
`;

const MenuItem = styled.li`
  position: relative;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: var(--app-font-size);
  font-weight: 500;
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  user-select: none;
  color: ${(props) => props.theme.navbarText || "#1e293b"};

  &:hover {
    background-color: ${(props) => props.theme.navbarHover || "#f1f5f9"};
    border-color: #e2e8f0;
  }

  ${({ $active, theme }) =>
    $active &&
    `
    background-color: ${theme.navbarHover || "#f1f5f9"};
    border-color: #cbd5e1;
  `}
`;

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  margin: 0;
  list-style: none;
  background-color: ${(props) => props.theme.navbarBackground || "#ffffff"};
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
  z-index: 1001;
  font-size: var(--app-font-size);
  min-width: 170px;
  animation: fadeInDown 0.12s ease-out;

  /* Puente invisible para evitar que se cierre el menú al mover el cursor hacia abajo */
  &::before {
    content: "";
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DropdownItem = styled.li`
  padding: 7px 12px;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  font-family: var(--font-body);
  font-size: var(--app-font-size);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 6px;
  color: #334155;
  transition: background-color 0.12s ease, color 0.12s ease;

  &:hover {
    background-color: ${(props) => props.theme.navbarHover || "#f1f5f9"};
    color: #0f172a;
  }
`;

const ArrowRight = styled.span`
  font-weight: bold;
  font-size: 13px;
  margin-left: 10px;
  color: #94a3b8;
`;

const NestedDropdown = styled.ul`
  position: absolute;
  top: -6px;
  left: 100%;
  margin: 0;
  list-style: none;
  background-color: ${(props) => props.theme.navbarBackground || "#ffffff"};
  padding: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
  z-index: 1002;
  font-size: var(--app-font-size);
  min-width: 160px;
  animation: fadeInRight 0.12s ease-out;

  /* Puente invisible horizontal */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: -10px;
    width: 10px;
  }

  @keyframes fadeInRight {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  gap: 16px;
`;

const SearchPillTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 12px;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
    color: #334155;
  }
`;

const SearchPillKbd = styled.kbd`
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  font-family: var(--font-mono);
  color: #64748b;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
`;

const PowerIcon = styled(FaPowerOff)`
  font-size: var(--app-font-size);
  cursor: pointer;
  color: #64748b;
  transition: color 0.15s ease;

  &:hover {
    color: #0f172a;
  }
`;

const Navbar = ({ userName }) => {
  const [fontSize, setFontSize] = useState(
    () => localStorage.getItem("fontSize") || "13px"
  );
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const navigate = useNavigate();

  const { hasPermission } = useAuth();
  const { logout } = useContext(AuthContext);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--app-font-size", fontSize);
    localStorage.setItem("fontSize", fontSize);
  }, [fontSize]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    const issues = validateMenuAgainstRoutes({ menu: menuConfig, routePermissions: ROUTE_PERMISSIONS, ignoreRoutes: [routes.error] });
    issues.forEach((m) => console.warn(`[Permisos/Menu] ${m}`));
  }, []);

  const leaveMenuTimeoutRef = useRef(null);
  const leaveSubMenuTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (leaveMenuTimeoutRef.current) clearTimeout(leaveMenuTimeoutRef.current);
      if (leaveSubMenuTimeoutRef.current) clearTimeout(leaveSubMenuTimeoutRef.current);
    };
  }, []);

  const closeAllMenus = () => {
    if (leaveMenuTimeoutRef.current) clearTimeout(leaveMenuTimeoutRef.current);
    if (leaveSubMenuTimeoutRef.current) clearTimeout(leaveSubMenuTimeoutRef.current);
    setOpenMenu(null);
    setOpenSubMenu(null);
  };

  const handleMouseEnterMenu = (menuName) => {
    if (leaveMenuTimeoutRef.current) {
      clearTimeout(leaveMenuTimeoutRef.current);
      leaveMenuTimeoutRef.current = null;
    }
    setOpenMenu((prev) => (prev === menuName ? prev : menuName));
  };

  const handleMouseLeaveMenu = () => {
    if (leaveMenuTimeoutRef.current) {
      clearTimeout(leaveMenuTimeoutRef.current);
    }
    leaveMenuTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
      setOpenSubMenu(null);
    }, 250);
  };

  const handleMouseEnterSubMenu = (subMenuName) => {
    if (leaveSubMenuTimeoutRef.current) {
      clearTimeout(leaveSubMenuTimeoutRef.current);
      leaveSubMenuTimeoutRef.current = null;
    }
    if (leaveMenuTimeoutRef.current) {
      clearTimeout(leaveMenuTimeoutRef.current);
      leaveMenuTimeoutRef.current = null;
    }
    setOpenSubMenu((prev) => (prev === subMenuName ? prev : subMenuName));
  };

  const handleMouseLeaveSubMenu = () => {
    if (leaveSubMenuTimeoutRef.current) {
      clearTimeout(leaveSubMenuTimeoutRef.current);
    }
    leaveSubMenuTimeoutRef.current = setTimeout(() => {
      setOpenSubMenu(null);
    }, 250);
  };

  const handleFontSizeChange = (size) => {
    setFontSize(size);
  };

  const handleLogout = async () => {
    await logout();
    navigate(routes.login);
  };

  // Centralized action handler
  const handleAction = (action) => {
    if (!action) return;
    if (action.type === "dispatchEvent") {
      window.dispatchEvent(new CustomEvent(action.event));
    } else if (action.type === "context") {
      if (action.func === "logout") handleLogout();
    } else if (action.type === "local") {
      if (action.func === "handleFontSizeChange") handleFontSizeChange(action.args[0]);
    }
  };

  const checkPerm = (item, parentRule) => {
    const rule = resolveMenuItemRule({
      item,
      parentRule,
      routePermissions: ROUTE_PERMISSIONS,
    });
    return canAccessByRule({ rule, hasPermission });
  };

  const renderDropdownItem = (item, parentRule) => {
    const rule = resolveMenuItemRule({
      item,
      parentRule,
      routePermissions: ROUTE_PERMISSIONS,
    });
    if (!canAccessByRule({ rule, hasPermission })) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isRealRoute = typeof item.route === "string" && item.route.startsWith("/");

    const onClick = (e) => {
      e.stopPropagation(); // Evitar cerrar prematuramente en clicks hijos
      if (!hasChildren) closeAllMenus();
      if (item.action) handleAction(item.action);
      if (isRealRoute && !hasChildren && !item.action) navigate(item.route);
    };

    if (hasChildren) {
      return (
        <DropdownItem
          key={item.id}
          onMouseEnter={() => handleMouseEnterSubMenu(item.id)}
          onMouseMove={() => handleMouseEnterSubMenu(item.id)}
          onMouseLeave={handleMouseLeaveSubMenu}
          onClick={onClick}
        >
          {item.label} <ArrowRight>›</ArrowRight>
          {openSubMenu === item.id && (
            <NestedDropdown>
              {item.children.map((child) => renderDropdownItem(child, rule))}
            </NestedDropdown>
          )}
        </DropdownItem>
      );
    }

    return (
      <DropdownItem
        key={item.id}
        onMouseEnter={() => {
          if (leaveMenuTimeoutRef.current) {
            clearTimeout(leaveMenuTimeoutRef.current);
            leaveMenuTimeoutRef.current = null;
          }
          if (leaveSubMenuTimeoutRef.current) {
            clearTimeout(leaveSubMenuTimeoutRef.current);
            leaveSubMenuTimeoutRef.current = null;
          }
          setOpenSubMenu(null);
        }}
        onMouseMove={() => {
          if (leaveMenuTimeoutRef.current) {
            clearTimeout(leaveMenuTimeoutRef.current);
            leaveMenuTimeoutRef.current = null;
          }
        }}
        onClick={onClick}
      >
        {item.label}
      </DropdownItem>
    );
  };

  return (
    <NavbarContainer>
      <LeftSection>
        {hasPermission(PATHS.INICIO) && (
          <img 
            src={icons["home"]} 
            alt="Inicio" 
            title="Inicio" 
            style={{ width: 16, height: 16, cursor: "pointer" }} 
            className="hover" 
            onClick={() => navigate(routes.home)} 
          />
        )}
        <Menu>
          {menuConfig.map((menu) => {
            if (!checkPerm(menu)) return null;

            const hasDropdown = menu.children && menu.children.length > 0;

            if (hasDropdown) {
              return (
                <MenuItem
                  key={menu.id}
                  $active={openMenu === menu.id}
                  onMouseEnter={() => handleMouseEnterMenu(menu.id)}
                  onMouseMove={() => handleMouseEnterMenu(menu.id)}
                  onMouseLeave={handleMouseLeaveMenu}
                >
                  {menu.label}
                  {openMenu === menu.id && (
                    <Dropdown>
                      {menu.children.map((child) => renderDropdownItem(child, resolveMenuItemRule({ item: menu, parentRule: null, routePermissions: ROUTE_PERMISSIONS })))}
                    </Dropdown>
                  )}
                </MenuItem>
              );
            }

            return (
              <MenuItem 
                key={menu.id} 
                onClick={() => {
                  closeAllMenus();
                  if (menu.action) handleAction(menu.action);
                  if (typeof menu.route === "string" && menu.route.startsWith("/") && !menu.action) navigate(menu.route);
                }}
              >
                {menu.label}
              </MenuItem>
            );
          })}
        </Menu>
      </LeftSection>

      <RightSection>

        <span>{userName}</span>
        {userName && (
          <PowerIcon className="hover" onClick={handleLogout} />
        )}
      </RightSection>

      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onAction={handleAction}
      />
    </NavbarContainer>
  );
};

export default Navbar;
